import { RootFolderTree } from '@workspace/view-model-folder/domain/FolderTree';
import type { FolderId, ViewModelId, WorkspaceId, RootFolderTreeJSON } from 'balus-common';
import type { DataSnapshot } from '@framework/repository';
import { RefBuilder } from '@framework/repository';
import { RTDBPath } from 'balus-common';

type UpdateValues = Record<string, string | null>;

export class RootFolderTreeRepository {
  private readonly path: string;
  private callback: ((value: DataSnapshot) => void) | undefined = undefined;

  constructor(workspaceId: WorkspaceId) {
    this.path = RTDBPath.Workspace.rootFolderTreePath(workspaceId);
  }

  async load(): Promise<RootFolderTree> {
    const snapshot = await RefBuilder.ref(this.path).once('value');
    const j = snapshot.val() as RootFolderTreeJSON;
    return j ? RootFolderTree.load(j) : RootFolderTree.buildEmpty();
  }

  on(callback: (rootFolderTree: RootFolderTree) => void): void {
    this.callback = RefBuilder.ref(this.path).on('value', (snapshot) => {
      const j = snapshot.val() as RootFolderTreeJSON;
      callback(j ? RootFolderTree.load(j) : RootFolderTree.buildEmpty());
    });
  }

  off(): void {
    if (this.callback) {
      RefBuilder.ref(this.path).off('value', this.callback);
      this.callback = undefined;
    }
  }

  private folderPath(folderId: FolderId): string {
    return [this.path, 'folderIdMap', folderId].join('/');
  }

  private viewModelPath(viewModelId: ViewModelId): string {
    return [this.path, 'viewModelIdMap', viewModelId].join('/');
  }

  async addFolder(parentFolderId: FolderId, folderId: FolderId): Promise<void> {
    await RefBuilder.ref().update(this.buildAddFolderUpdateValues(parentFolderId, folderId));
  }

  buildAddFolderUpdateValues(parentFolderId: FolderId, folderId: FolderId): UpdateValues {
    return { [this.folderPath(folderId)]: parentFolderId };
  }

  async addViewModel(parentFolderId: FolderId, viewModelId: ViewModelId): Promise<void> {
    await RefBuilder.ref().update(this.buildAddViewModelUpdateValues(parentFolderId, viewModelId));
  }

  buildAddViewModelUpdateValues(parentFolderId: FolderId, viewModelId: ViewModelId): UpdateValues {
    return { [this.viewModelPath(viewModelId)]: parentFolderId };
  }

  async moveFolder(folderId: FolderId, targetFolderId: FolderId): Promise<void> {
    await RefBuilder.ref().update({ [this.folderPath(folderId)]: targetFolderId });
  }

  async moveViewModel(viewModelId: ViewModelId, targetFolderId: FolderId): Promise<void> {
    await RefBuilder.ref().update({ [this.viewModelPath(viewModelId)]: targetFolderId });
  }

  async removeFolder(folderId: FolderId): Promise<void> {
    await RefBuilder.ref().update(await this.buildRemoveFolderUpdateValues(folderId));
  }

  async buildRemoveFolderUpdateValues(folderId: FolderId): Promise<UpdateValues> {
    const updates: UpdateValues = {};

    // 現時点のツリーを取得して、削除対象のフォルダを取得する
    const tree = await this.load();
    const folder = tree.findFolder(folderId);

    // 削除対象のフォルダが見つからなければ、何もする必要が無い
    if (!folder) {
      return {};
    }

    // 削除対象フォルダと、その子孫のフォルダとビューモデルを全てツリーから取り除く
    updates[this.folderPath(folderId)] = null;
    folder.descendantsFolderIds().forEach((id) => {
      updates[this.folderPath(id)] = null;
    });
    folder.descendantsViewModelIds().forEach((id) => {
      updates[this.viewModelPath(id)] = null;
    });

    return updates;
  }

  async removeViewModel(viewModelId: ViewModelId): Promise<void> {
    await RefBuilder.ref().update(this.buildRemoveViewModel(viewModelId));
  }

  buildRemoveViewModel(viewModelId: ViewModelId): UpdateValues {
    return { [this.viewModelPath(viewModelId)]: null };
  }
}
